// UserItem.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import HighlightSubstring from '../component/HighlightSubstring.jsx';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import ChatIcon from '../../img/btn_chat_24.svg';
import TextEllipsis from '../style/TextEllipsis.js';
import leaderboardIcons from '../resource/getLeaderboardIcons.js';
import { ButtonId, ModalId } from '../resource/mixpanel.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

const srcSet = [
  MediaAssetFormat.AVIF,
  MediaAssetFormat.WEBP,
  MediaAssetFormat.JPG,
]
  .map(format => {
    return [
      { size: 64, resolution: '1x' },
      { size: 128, resolution: '2x' },
    ].map(size => ({ ...size, ext: format }));
  })
  .flatMap(a => a);

export const UserItem = ({
  userId = '',
  username = '',
  displayName = '',
  query = '',
  mixpanelModalId = null,
  place = null,
  isDisplayNameEnabled = true,
  isPostLikesModal = false,
  isPostSummaryModal = false,
  isUserFetching = false,
  isUserFetched = false,
  sendChatStart = () => null,
  fetchUser = () => null,
  onCloseModal = () => null,
  region = null,
  category = null,
  range = null,
}) => {
  useEffect(() => {
    if (
      userId &&
      (!username || (!displayName && !isUserFetching && !isUserFetched))
    ) {
      fetchUser({ userId });
    }
  }, [username, userId, fetchUser, displayName, isUserFetching, isUserFetched]);
  const placeIcon = leaderboardIcons.place[place];
  const rankBadge = leaderboardIcons.rank[place];

  return (
    <Link
      data-element_id={ButtonId.Leaderboard.ButtonLeaderboardUser}
      data-tracking_payload={{
        modalId: mixpanelModalId,
        'user.username': username,
        'leaderboard.rank': place,
        'leaderboard.region': region,
        'leaderboard.tab': category,
        'leaderboard.range': range,
      }}
      to={`/user/${userId}`}
      title={username}
      onClick={() => {
        onCloseModal();
      }}
    >
      <StyledUserItem>
        {place && (
          <RankWrapper>
            {placeIcon && <PlaceIcon src={placeIcon} alt="place icon" />}
            {rankBadge ? (
              <img
                src={rankBadge}
                alt={`Ranking: ${place}`}
                width="24"
                height="30"
              />
            ) : (
              place
            )}
          </RankWrapper>
        )}
        <UserAvatarWithStatus
          id={userId}
          srcSet={srcSet}
          size={48}
          circleOffset={2}
          livestreamSize={12}
          shouldShowLiveStream
          shouldShowOnline
        />
        <NameWrapper>
          {isDisplayNameEnabled && displayName ? (
            <>
              <HighlightSubstring substring={query}>
                {displayName}
              </HighlightSubstring>
              <SecondaryText>
                @
                <HighlightSubstring substring={query}>
                  {username}
                </HighlightSubstring>
              </SecondaryText>
            </>
          ) : (
            <>
              @
              <HighlightSubstring substring={query}>
                {username}
              </HighlightSubstring>
            </>
          )}
        </NameWrapper>
        <ChatButton
          data-element_id={ButtonId.All.ButtonChat}
          data-tracking_payload={{
            modalId: isPostLikesModal
              ? ModalId.Post.ModalPostLikeDetail
              : isPostSummaryModal
                ? ModalId.Post.ModalPostSummaryDetail
                : null,
          }}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onCloseModal();
            sendChatStart();
          }}
        >
          <img
            src={ChatIcon}
            alt={`chat with ${username}`}
            width="24"
            height="24"
          />
        </ChatButton>
      </StyledUserItem>
    </Link>
  );
};

UserItem.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  query: PropTypes.string,
  mixpanelModalId: PropTypes.string,
  place: PropTypes.number,
  isPostLikesModal: PropTypes.bool,
  isPostSummaryModal: PropTypes.bool,
  isUserFetching: PropTypes.bool,
  isUserFetched: PropTypes.bool,
  isDisplayNameEnabled: PropTypes.bool,
  sendChatStart: PropTypes.func,
  fetchUser: PropTypes.func,
  onCloseModal: PropTypes.func,
  region: PropTypes.string,
  category: PropTypes.string,
  range: PropTypes.string,
};

const StyledUserItem = styled.div`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
`;

const RankWrapper = styled.div`
  position: relative;
  min-width: 24px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const PlaceIcon = styled.img`
  position: absolute;
  top: 39%;
  width: 20px;
  height: 20px;
  transform: translateY(calc(-50% - 21px));
`;

const ChatButton = styled.button`
  width: 36px;
  height: 36px;
  padding: 6px;
  border-radius: 22px;
  outline: none;
  border: 0px;
  background-image: linear-gradient(135deg, #00d2be, #5d82bc 52%, #8e37d7);
  &:hover {
    opacity: 0.7;
  }
`;

const NameWrapper = styled.div`
  ${TextEllipsis}
  color: var(--font-color-primary-on-dark, #fff);
  margin: 0 12px;
  flex-grow: 1;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.024px;
`;

const SecondaryText = styled.span`
  color: var(--font-color-secondary-on-dark, rgba(221, 221, 221, 0.8));
  padding-left: 2px;
  font-weight: 400;
`;

export default UserItem;
